import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, CheckCircle, Building2, Microscope, Database, Shield } from 'lucide-react';

const practiceAreas = [
  { title: 'SaaS & Software Licensing', description: 'Enterprise software agreements, subscription models, and platform terms.', href: '/practice-areas#saas' },
  { title: 'Data Use & Licensing', description: 'Structuring data access, licensing, and commercialization arrangements.', href: '/practice-areas#data' },
  { title: 'Life Sciences Transactions', description: 'Technology agreements with pharmaceutical enterprises.', href: '/practice-areas#life-sciences' },
  { title: 'IP Allocation', description: 'Structuring intellectual property ownership in technology transactions.', href: '/practice-areas#ip' },
  { title: 'Privacy & Data Protection', description: 'HIPAA, GDPR, and compliance-focused provisions.', href: '/practice-areas#privacy' },
  { title: 'Vendor-Side Risk Allocation', description: 'Protecting technology vendors in enterprise negotiations.', href: '/practice-areas#vendor-risk' },
];

const industries = [
  { icon: Microscope, name: 'Life Sciences & Pharma' },
  { icon: Building2, name: 'Healthcare Technology' },
  { icon: Database, name: 'SaaS & Data Platforms' },
  { icon: Shield, name: 'Clinical Analytics' },
];

const stats = [
  { number: '20+', label: 'Years Experience' },
  { number: '500+', label: 'Transactions Handled' },
  { number: '100%', label: 'Vendor-Side Focus' },
];

export const HomePage = () => {
  return (
    <div data-testid="homepage">
      {/* Hero Section */}
      <section className="hero-section py-20 md:py-28" data-testid="hero-section">
        <div className="container-law relative z-10">
          <div className="max-w-3xl">
            <div className="breadcrumb text-white/60 mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span>Commercial & Technology Law</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6">
              Legal Counsel for Companies Powering<br />
              <span className="text-green">Life Sciences</span><br />
              Innovation
            </h1>
            <p className="text-lg md:text-xl text-white/80 leading-relaxed mb-8 max-w-2xl">
              Inside counsel capability, delivered through an outside counsel model. We serve the technology and data companies that pharmaceutical and biotech enterprises depend on to develop, test, and commercialize new therapies.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="btn-green" data-testid="hero-cta-primary">
                Discuss Your Matter
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
              </Link>
              <Link to="/practice-areas" className="btn-outline border-white text-white hover:bg-white hover:text-navy" data-testid="hero-cta-secondary">
                Our Practice Areas
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Box */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container-law">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="feature-box">
              <h3 className="font-heading text-xl text-navy mb-3">Not a Generic Software Vendor Practice</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                We understand how platforms function, how data is generated and processed, how services are delivered, and how customers use products in clinical, regulatory, or commercial workflows.
              </p>
              <Link to="/why-us" className="text-green font-medium text-sm flex items-center gap-1 hover:gap-2 transition-all">
                Learn More <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </Link>
            </div>
            <div className="lg:col-span-2 bg-white border border-gray-200 p-8">
              <h3 className="font-heading text-2xl text-navy mb-4">Who We Serve</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Our clients build the software platforms, data assets, and specialized services that pharmaceutical and biotech companies depend on. They operate globally, sit close to regulated data, and sell mission-critical solutions into highly complex environments.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Many combine SaaS offerings with professional services, data licensing, and long-term enterprise engagements. We help them develop baseline agreement terms and negotiation playbooks that align with their products, services, and regulatory posture.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 border-b border-gray-200">
        <div className="container-law">
          <div className="grid grid-cols-3 divide-x divide-gray-200">
            {stats.map((stat, index) => (
              <div key={index} className="stat-item">
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Practice Areas */}
      <section className="py-16 md:py-24" data-testid="practice-areas-preview">
        <div className="container-law">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <p className="text-green font-medium text-sm uppercase tracking-wider mb-2">What We Do</p>
              <h2 className="font-heading text-3xl md:text-4xl text-navy">Practice Areas</h2>
            </div>
            <Link to="/practice-areas" className="text-navy font-medium text-sm flex items-center gap-1 hover:text-green transition-colors">
              View All Practice Areas <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {practiceAreas.map((area, index) => (
              <Link
                key={area.title}
                to={area.href}
                className="practice-card group"
                data-testid={`practice-area-card-${index}`}
              >
                <h3 className="font-heading text-lg text-navy mb-2 group-hover:text-green transition-colors">
                  {area.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  {area.description}
                </p>
                <span className="text-green font-medium text-sm flex items-center gap-1">
                  Learn more <ChevronRight className="w-4 h-4" strokeWidth={2} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-16 md:py-24 bg-gray-50" data-testid="industries-preview">
        <div className="container-law">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-green font-medium text-sm uppercase tracking-wider mb-2">Industries</p>
              <h2 className="font-heading text-3xl md:text-4xl text-navy mb-6">
                Built for the Intersection of Technology, Data, and Life Sciences
              </h2>
              <p className="text-gray-600 leading-relaxed mb-8">
                We routinely handle agreements involving global deployments, regulated data, cross-border transfers, validation requirements, inspection readiness, and long-term commercial relationships with large pharmaceutical and biotech customers.
              </p>
              <Link to="/industries" className="btn-navy" data-testid="view-industries">
                Explore Industries
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {industries.map((industry, index) => (
                <div key={index} className="bg-white border border-gray-200 p-5 flex items-center gap-4">
                  <div className="w-12 h-12 bg-green/10 flex items-center justify-center flex-shrink-0">
                    <industry.icon className="w-6 h-6 text-green" strokeWidth={1.5} />
                  </div>
                  <span className="font-medium text-navy text-sm">{industry.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-16 md:py-24 section-navy" data-testid="why-us-preview">
        <div className="container-law">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <p className="text-green font-medium text-sm uppercase tracking-wider mb-2">Why Erlichman Law</p>
              <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
                Inside Counsel Depth. Outside Counsel Flexibility.
              </h2>
              <p className="text-white/70 leading-relaxed mb-8">
                Traditional outside firms often approach these deals without product or domain context, resulting in over-lawyering, unnecessary escalations, and inconsistent positions. Our model is different. We act as an extension of your internal legal and business teams.
              </p>
              <Link to="/why-us" className="btn-green" data-testid="learn-more-why-us">
                Learn More <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
              </Link>
            </div>
            <div className="space-y-6">
              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-green flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <h3 className="text-white font-medium mb-1">We Know the Recurring Issues</h3>
                  <p className="text-white/60 text-sm">Risk tolerances, internal decision makers, and the business rationale behind each position.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-green flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <h3 className="text-white font-medium mb-1">Institutional Knowledge Over Time</h3>
                  <p className="text-white/60 text-sm">That knowledge reduces friction, shortens deal cycles, and produces consistent outcomes across transactions.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-green flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <h3 className="text-white font-medium mb-1">Single, Integrated Legal Function</h3>
                  <p className="text-white/60 text-sm">The depth of inside counsel and the flexibility of outside counsel—combined.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20" data-testid="cta-section">
        <div className="container-law">
          <div className="bg-gray-50 border border-gray-200 p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl text-navy mb-2">
                Supporting the companies that power modern drug development
              </h2>
              <p className="text-gray-600">
                Inside counsel depth. Outside counsel flexibility. A single, integrated legal function.
              </p>
            </div>
            <Link to="/contact" className="btn-navy whitespace-nowrap" data-testid="cta-contact-btn">
              Start a Conversation
              <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
